/**
 * L'elenco della pagina Passività: in che ordine si mostrano i documenti dei
 * fornitori e quanto fanno, per tipo.
 *
 * I totali seguono le regole dei costi di Analisi aziendali
 * (kpiFatturato.ts, segnoCosto): le note di credito dei fornitori si tolgono,
 * i DDT ricevuti non sono costi, e senza imponibile si usa il totale. Prima
 * la pagina sommava tutto con l'IVA, note di credito comprese col segno
 * sbagliato.
 *
 * Le fatture ancora "da registrare" su Fatture in Cloud (fic_pending_id
 * pieno) si mostrano ma non sono costi: hanno un totale a parte.
 */

import { segnoCosto } from '../dashboard/kpiFatturato';
import { confrontaDataNumero, sommaVuota, type Somma } from './elencoFatture';

/** Le colonne che servono a ordinare e a fare i conti. */
export interface DocumentoPassivo {
  /** Pieno = ancora da registrare su Fatture in Cloud. */
  fic_pending_id?: number | null;
  document_type: string | null;
  document_date: string | null;
  document_number: string | null;
  supplier_name: string | null;
  taxable_amount: number | null;
  total_amount: number | null;
}

export interface RiepilogoPassive {
  /** Fatture e spese dei fornitori. */
  spese: Somma;
  /** Importi positivi: nei costi si tolgono. */
  noteCredito: Somma;
  /** Spese meno note di credito: i costi di Analisi aziendali. */
  netto: Somma;
  /** Non sono costi: si contano soltanto. */
  ddt: number;
  /** Arrivate ma non ancora registrate su Fatture in Cloud: non sono costi. */
  daRegistrare: Somma;
}

export type ChiaveStato = 'da_registrare' | 'pagata' | 'da_pagare' | 'scaduta' | 'stornata' | 'registrata';

export interface StatoPassiva {
  chiave: ChiaveStato;
  etichetta: string;
}

/**
 * Lo stato da mostrare nella bolla della riga. `oggi` e' AAAA-MM-GG.
 * payment_status arriva da Fatture in Cloud: paid, pending, reversed, o
 * vuoto se Fatture in Cloud non ha mandato i pagamenti.
 */
export function statoPassiva(
  doc: { fic_pending_id?: number | null; payment_status: string | null; due_date: string | null },
  oggi: string,
): StatoPassiva {
  if (doc.fic_pending_id != null) return { chiave: 'da_registrare', etichetta: 'Da registrare' };
  const s = (doc.payment_status ?? '').toLowerCase();
  if (s === 'reversed') return { chiave: 'stornata', etichetta: 'Stornata' };
  if (s === 'pending' || s === 'not_paid') {
    return doc.due_date && doc.due_date < oggi
      ? { chiave: 'scaduta', etichetta: 'Scaduta' }
      : { chiave: 'da_pagare', etichetta: 'Da pagare' };
  }
  if (s === 'paid') return { chiave: 'pagata', etichetta: 'Pagata' };
  return { chiave: 'registrata', etichetta: 'Registrata' };
}

/**
 * Per Array.sort: data piu' recente prima, poi numero piu' alto, poi
 * fornitore in ordine alfabetico.
 */
export function confrontaPassive(a: DocumentoPassivo, b: DocumentoPassivo): number {
  return (
    confrontaDataNumero({ data: a.document_date, numero: a.document_number }, { data: b.document_date, numero: b.document_number }) ||
    (a.supplier_name ?? '').localeCompare(b.supplier_name ?? '', 'it')
  );
}

/** I totali dei documenti dati, separati per tipo. */
export function riepilogoPassive(documenti: readonly DocumentoPassivo[]): RiepilogoPassive {
  let spese = sommaVuota();
  let noteCredito = sommaVuota();
  let daRegistrare = sommaVuota();
  let ddt = 0;

  for (const doc of documenti) {
    const totale = doc.total_amount ?? 0;
    // Come in Analisi aziendali: senza imponibile si ripiega sul totale.
    const imponibile = doc.taxable_amount ?? totale;
    const voce = { documenti: 1, imponibile, totale };
    if (doc.fic_pending_id != null) {
      daRegistrare = somma(daRegistrare, voce);
      continue;
    }
    const segno = segnoCosto(doc.document_type);
    if (segno === 0) ddt += 1;
    else if (segno < 0) noteCredito = somma(noteCredito, voce);
    else spese = somma(spese, voce);
  }

  const netto: Somma = {
    documenti: spese.documenti + noteCredito.documenti,
    imponibile: spese.imponibile - noteCredito.imponibile,
    totale: spese.totale - noteCredito.totale,
  };
  return { spese, noteCredito, netto, ddt, daRegistrare };
}

const somma = (a: Somma, b: Somma): Somma => ({
  documenti: a.documenti + b.documenti,
  imponibile: a.imponibile + b.imponibile,
  totale: a.totale + b.totale,
});
